import React, { useEffect, useMemo } from 'react';
import { ThemeProvider } from '@emotion/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AlertTitle, StyledEngineProvider } from '@mui/material';
import { godzilla as theme, ThemeContext } from 'lib';
import {
    PermissionContextProvider,
    useAuth,
    ServiceContextProvider,
} from 'core/contexts';
import { useAlert } from '../../../../core';
import { AppInitializer } from '../app-initializer/AppInitializer';
import { ReactQueryDevtools } from 'react-query/devtools';
import { useSearchParams } from 'react-router-dom';
import { HelixLoaderComponent } from 'components';
import { RealmInputModal } from 'core/components';
import { APP_FONTS } from '../../../../constants';
import { ThemeProvider as ApexThemeProvider } from '@biq/apex-ui';

export const App = (): React.JSX.Element => {
    const {
        init,
        isInitialized,
        keycloakLoading,
        keycloakError,
        isAuthenticated,
    } = useAuth();
    let [searchParams] = useSearchParams();
    const searchParamsRealm = searchParams.get('realm');
    const { showAlert, hideAlert } = useAlert();
    const clientId: string = process.env.REACT_APP_CLIENT_ID ?? '';
    const url: string = process.env.REACT_APP_KEYCLOAK_URL ?? '';
    const dedicatedEnv =
        (process.env.REACT_APP_DEDICATED_ENV_TYPE ?? 'false') === 'true';
    const dedicatedRealm = process.env.REACT_APP_AUTH_REALM ?? '';

    if (searchParamsRealm) {
        localStorage.setItem('realm', searchParamsRealm);
    } else if (dedicatedRealm && dedicatedEnv) {
        localStorage.setItem('realm', dedicatedRealm);
    }

    useEffect(() => {
        const disableDragging = (): void => {
            const images = document.querySelectorAll('img');
            for (const img of Array.from(images)) {
                img.setAttribute('draggable', 'false');
            }
        };

        // Call once initially
        disableDragging();

        // Also observe DOM changes in case React loads more images later
        const observer = new MutationObserver(disableDragging);
        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        for (const font of APP_FONTS) {
            for (const weight of font.weights) {
                import(`@fontsource/${font.family}/${weight}.css`);
            }
        }
    }, []);

    useEffect(() => {
        const r = localStorage.getItem('realm');
        if (r) {
            const keyCloakConfig = {
                realm: r,
                clientId: clientId,
                url: url,
            };
            init(keyCloakConfig);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (keycloakError) {
            showAlert({
                severity: 'error',
                variant: 'filled',
                closable: true,
                onClose: hideAlert,
                children: (
                    <>
                        <AlertTitle>Keycloak error</AlertTitle>
                        {keycloakError}
                    </>
                ),
            });
        }
    }, [keycloakError]); // eslint-disable-line react-hooks/exhaustive-deps

    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
            },
            mutations: {
                onMutate: (): void => {
                    hideAlert();
                },
            },
        },
    });

    useEffect(() => {
        const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
            if (event?.query?.state?.isFetching) {
                hideAlert();
            }
        });
        return unsubscribe;
    }, [queryClient]);

    const currentTheme = useMemo(() => ({ currentTheme: theme }), []);
    const displayRealmSelection =
        !dedicatedEnv &&
        !searchParamsRealm &&
        !localStorage.getItem('realm') &&
        !isAuthenticated();

    if (isInitialized) {
        return (
            <StyledEngineProvider injectFirst>
                <ThemeContext.Provider value={currentTheme}>
                    <ThemeProvider theme={theme}>
                        {/**
                         * ApexThemeProvider allows us to override the current mode (light or dark)
                         * or pass a custom theme to the apex-ui library. The component library uses
                         * its own light mode by default.
                         */}
                        <ApexThemeProvider>
                            <QueryClientProvider client={queryClient}>
                                <PermissionContextProvider>
                                    <ServiceContextProvider>
                                        <AppInitializer />
                                        <ReactQueryDevtools
                                            initialIsOpen={false}
                                            position='bottom-right'
                                        />
                                    </ServiceContextProvider>
                                </PermissionContextProvider>
                            </QueryClientProvider>
                        </ApexThemeProvider>
                    </ThemeProvider>
                </ThemeContext.Provider>
            </StyledEngineProvider>
        );
    } else if (keycloakLoading) {
        return <HelixLoaderComponent />;
    } else if (displayRealmSelection) {
        return (
            <ThemeProvider theme={theme}>
                <RealmInputModal />
            </ThemeProvider>
        );
    }

    return <></>;
};
